import {
    type BatchCall,
    type ClaimKind,
    type FindPathResponse,
    type CreatePoolParams,
} from '@aquariusdefi/sdk';
import { scValToNative, type Transaction, type xdr } from '@stellar/stellar-sdk';

import { AMM_CONTRACT_METHOD as METHOD } from 'constants/soroban';
import { ACCOUNT_FOR_SIMULATE } from 'constants/stellar-accounts';

import { fromBaseUnits, toBaseUnits } from 'helpers/amount';
import { getAssetString } from 'helpers/assets';

import Connection from 'services/soroban/connection/connection';
import TokenContract from 'services/soroban/contracts/tokenContract';
import {
    getAquariusClient,
    getAquariusPool,
    toAquariusAsset,
} from 'services/soroban/utils/aquariusSdk.lazy';
import { buildBatchCall, simulateBatch } from 'services/soroban/utils/batchSimulation';
import {
    amountsForTokens,
    toDepositAmounts,
    amountToUint128,
    publicKeyToScVal,
    tickToScVal,
    toTokenAmountsScVal,
} from 'services/soroban/utils/scValHelpers';

import {
    ConcentratedPoolInfo,
    ConcentratedPosition,
    ConcentratedUserPositionSnapshot,
    ConcentratedSlot0,
    PoolCreationFeeInfo,
    PoolExtended,
    PoolIncentives,
    PoolProcessed,
    PoolRewardsInfo,
    RewardType,
} from 'types/amm';
import { Token } from 'types/token';

/** UI amounts and wallet transactions adapted to the Aquarius SDK. */
export default class AmmContract {
    constructor(
        private readonly connection: Connection,
        private readonly token: TokenContract,
    ) {}

    private build(build: () => Promise<string>): Promise<Transaction> {
        return this.connection.prepareSdkTransaction(build);
    }

    private batch(accountId: string, calls: BatchCall[], unitSize = 1, maxCallsPerBatch?: number) {
        return simulateBatch(accountId, calls, {
            unitSize,
            maxCallsPerBatch,
        });
    }

    private async read(
        poolId: string,
        method: string,
        args: xdr.ScVal[] = [],
        accountId = ACCOUNT_FOR_SIMULATE,
    ) {
        const [result] = await this.batch(accountId, [buildBatchCall(poolId, method, args)]);
        return result;
    }

    private async create(
        accountId: string,
        params: CreatePoolParams,
        createInfo: PoolCreationFeeInfo,
    ) {
        let poolAddress: string;
        const tx = await this.build(async () => {
            const feeAmount =
                params.type === 'volatile'
                    ? createInfo.constantFee
                    : params.type === 'stable'
                      ? createInfo.stableFee
                      : createInfo.concentratedFee;
            const built = await (
                await getAquariusClient(accountId)
            ).pools.buildCreate({
                ...params,
                expectedCreationFee: {
                    token: createInfo.token.contract,
                    destination: createInfo.destination,
                    amount: toBaseUnits(feeAmount, createInfo.token.decimal),
                },
            });
            poolAddress = built.pool.address;
            return built.xdr;
        });
        return { tx, poolAddress };
    }

    async getCreateAndDepositConstantPoolTx(
        accountId: string,
        base: Token,
        counter: Token,
        fee: number,
        createInfo: PoolCreationFeeInfo,
        amounts: Map<string, string>,
    ) {
        return this.create(
            accountId,
            {
                type: 'volatile',
                tokens: await Promise.all([base, counter].map(toAquariusAsset)),
                feeBps: fee,
                amounts: toDepositAmounts([base, counter], amounts),
            },
            createInfo,
        );
    }

    async getCreateAndDepositStablePoolTx(
        accountId: string,
        assets: Token[],
        fee: number,
        createInfo: PoolCreationFeeInfo,
        amounts: Map<string, string>,
    ) {
        return this.create(
            accountId,
            {
                type: 'stable',
                tokens: await Promise.all(assets.map(toAquariusAsset)),
                feeBps: Math.round(fee * 100),
                amounts: toDepositAmounts(assets, amounts),
            },
            createInfo,
        );
    }

    async getCreateAndDepositConcentratedPoolTx(
        accountId: string,
        base: Token,
        counter: Token,
        fee: number,
        createInfo: PoolCreationFeeInfo,
        tickLower: number,
        tickUpper: number,
        amounts: Map<string, string>,
    ) {
        return this.create(
            accountId,
            {
                type: 'concentrated',
                tokens: await Promise.all([base, counter].map(toAquariusAsset)),
                feeBps: fee,
                amounts: toDepositAmounts([base, counter], amounts),
                tickRange: [tickLower, tickUpper],
            },
            createInfo,
        );
    }

    private parseRewards(raw: Record<string, bigint>, decimals = 7): PoolRewardsInfo {
        return Object.fromEntries(
            Object.entries(raw).map(([key, value]) => [
                key,
                ['exp_at', 'last_time', 'expired_at'].includes(key)
                    ? Number(value)
                    : fromBaseUnits(value, decimals),
            ]),
        ) as PoolRewardsInfo;
    }

    async getPoolRewards(accountId: string, poolId: string): Promise<PoolRewardsInfo> {
        const pool = await getAquariusPool(poolId, accountId);
        return this.parseRewards((await pool.rewardsInfo(accountId)).raw);
    }

    async getPoolIncentives(accountId: string, poolId: string): Promise<PoolIncentives[]> {
        const pool = await getAquariusPool(poolId, accountId);
        return Promise.all(
            (await pool.incentives(accountId)).map(async info => {
                const token = await this.token.parseTokenContractId(info.token);
                return {
                    token,
                    info: {
                        expired_at: info.expiresAt.toString(),
                        tps: fromBaseUnits(info.tps, token.decimal),
                        user_reward: fromBaseUnits(info.userReward, token.decimal),
                    },
                };
            }),
        );
    }

    async getPoolsRewards(accountId: string, pools: string[]) {
        const results = await this.batch(
            accountId,
            pools.map(pool =>
                buildBatchCall(pool, METHOD.GET_REWARDS_INFO, [publicKeyToScVal(accountId)]),
            ),
        );
        return results.map(value => this.parseRewards(scValToNative(value)));
    }

    async getPoolsIncentives(accountId: string, pools: string[]): Promise<PoolIncentives[][]> {
        const results = await this.batch(
            accountId,
            pools.map(pool =>
                buildBatchCall(pool, METHOD.GET_INCENTIVES_INFO, [publicKeyToScVal(accountId)]),
            ),
        );
        return Promise.all(
            results.map(value =>
                Promise.all(
                    Object.entries(
                        scValToNative(value) as Record<string, Record<string, bigint>>,
                    ).map(async ([id, info]) => {
                        const token = await this.token.parseTokenContractId(id);
                        return {
                            token,
                            info: {
                                expired_at: String(info.expired_at),
                                tps: fromBaseUnits(info.tps, token.decimal),
                                user_reward: fromBaseUnits(info.user_reward, token.decimal),
                            },
                        };
                    }),
                ),
            ),
        );
    }

    async getUserPositionSnapshots(
        user: string,
        poolIds: string[],
    ): Promise<Map<string, ConcentratedUserPositionSnapshot | null>> {
        const results = await this.batch(
            ACCOUNT_FOR_SIMULATE,
            poolIds.map(id =>
                buildBatchCall(id, METHOD.GET_USER_POSITION_SNAPSHOT, [publicKeyToScVal(user)]),
            ),
            1,
            10,
        );
        return new Map(
            poolIds.map((id, i) => [
                id,
                scValToNative(results[i]) as ConcentratedUserPositionSnapshot,
            ]),
        );
    }

    async getConcentratedPositions(
        owner: string,
        requests: { poolId: string; tickLower: number; tickUpper: number }[],
    ): Promise<(ConcentratedPosition | null)[]> {
        const results = await this.batch(
            ACCOUNT_FOR_SIMULATE,
            requests.map(({ poolId, tickLower, tickUpper }) =>
                buildBatchCall(poolId, METHOD.GET_POSITION, [
                    publicKeyToScVal(owner),
                    tickToScVal(tickLower),
                    tickToScVal(tickUpper),
                ]),
            ),
            1,
            10,
        );
        return results.map((value, i) => {
            const raw = scValToNative(value);
            return raw
                ? {
                      tickLower: requests[i].tickLower,
                      tickUpper: requests[i].tickUpper,
                      liquidity: String(raw.liquidity ?? raw.L ?? 0n),
                  }
                : null;
        });
    }

    async getPositionsWithdrawAndFees(
        accountId: string,
        items: {
            poolId: string;
            tokens: Token[];
            tickLower: number;
            tickUpper: number;
            liquidity: string;
        }[],
    ): Promise<{ withdrawEstimates: string[]; feeEstimates: string[] }[]> {
        const calls = items.flatMap(({ poolId, tickLower, tickUpper, liquidity }) => [
            buildBatchCall(poolId, METHOD.ESTIMATE_WITHDRAW_POSITION, [
                publicKeyToScVal(accountId),
                tickToScVal(tickLower),
                tickToScVal(tickUpper),
                amountToUint128(liquidity, 0),
            ]),
            buildBatchCall(poolId, METHOD.GET_POSITION_FEES, [
                publicKeyToScVal(accountId),
                tickToScVal(tickLower),
                tickToScVal(tickUpper),
            ]),
        ]);
        const results = await this.batch(accountId, calls, 2, 8);
        return items.map((item, i) => {
            const ordered = this.token.orderTokens(item.tokens);
            const ids = ordered.map(token => token.contract);
            return {
                withdrawEstimates: amountsForTokens(
                    scValToNative(results[i * 2]),
                    ids,
                    item.tokens,
                ),
                feeEstimates: amountsForTokens(scValToNative(results[i * 2 + 1]), ids, item.tokens),
            };
        });
    }

    async getPoolsAllPositionFees(
        accountId: string,
        pools: { address: string; tokens: Token[] }[],
    ): Promise<Map<string, string[]>> {
        const results = await this.batch(
            accountId,
            pools.map(({ address }) =>
                buildBatchCall(address, METHOD.GET_ALL_POSITION_FEES, [
                    publicKeyToScVal(accountId),
                ]),
            ),
        );
        return new Map(
            pools.map(({ address, tokens }, i) => {
                const ordered = this.token.orderTokens(tokens);
                return [
                    address,
                    amountsForTokens(
                        scValToNative(results[i]),
                        ordered.map(token => token.contract),
                        tokens,
                    ),
                ];
            }),
        );
    }

    async getTotalShares(poolId: string) {
        return fromBaseUnits(await (await getAquariusPool(poolId)).totalShares());
    }

    getClaimRewardsTx(accountId: string, poolId: string) {
        return this.build(async () =>
            (await getAquariusPool(poolId, accountId)).buildClaimRewards(),
        );
    }

    getClaimIncentiveTx(accountId: string, poolId: string) {
        return this.build(async () =>
            (await getAquariusPool(poolId, accountId)).buildClaimIncentives(),
        );
    }

    getClaimBatchTx(accountId: string, poolsAndTypes: string[]) {
        return this.build(async () =>
            (await getAquariusClient(accountId)).buildClaim(
                await Promise.all(
                    poolsAndTypes.map(async item => {
                        const [type, address] = item.split('-');
                        const kind: ClaimKind =
                            type === RewardType.aquaReward
                                ? 'rewards'
                                : type === RewardType.positionFees
                                  ? 'positionFees'
                                  : 'incentives';
                        return { pool: await getAquariusPool(address, accountId), kind };
                    }),
                ),
            ),
        );
    }

    getWithdrawAndClaim(
        accountId: string,
        poolAddress: string,
        shares: string,
        _assets: Token[],
        _shareAddress: string,
        claimRewards: boolean,
        claimIncentives: boolean,
    ) {
        return this.build(async () =>
            (await getAquariusPool(poolAddress, accountId)).buildWithdrawAndClaim({
                shares: toBaseUnits(shares),
                claimRewards,
                claimIncentives,
            }),
        );
    }

    getSingleTokenWithdrawAndClaim(
        accountId: string,
        poolAddress: string,
        shares: string,
        selectedToken: Token,
        minimumAmount: string,
        _shareAddress: string,
        claimRewards: boolean,
        claimIncentives: boolean,
    ) {
        return this.build(async () =>
            (await getAquariusPool(poolAddress, accountId)).buildWithdrawOneCoin({
                shares: toBaseUnits(shares),
                token: selectedToken.contract,
                minimumAmount: toBaseUnits(minimumAmount, selectedToken.decimal),
                claimRewards,
                claimIncentives,
            }),
        );
    }

    getCustomWithdrawAndClaim(
        accountId: string,
        poolAddress: string,
        maximumShares: string,
        amounts: Map<string, string>,
        tokens: Token[],
        _shareAddress: string,
        claimRewards: boolean,
        claimIncentives: boolean,
    ) {
        return this.build(async () =>
            (await getAquariusPool(poolAddress, accountId)).buildWithdrawImbalanced({
                amounts: toDepositAmounts(tokens, amounts),
                maximumShares: toBaseUnits(maximumShares),
                claimRewards,
                claimIncentives,
            }),
        );
    }

    async estimateCustomWithdraw(
        accountId: string,
        poolAddress: string,
        _maximumShares: string,
        amounts: Map<string, string>,
        tokens: Token[],
        _shareAddress: string,
    ) {
        return fromBaseUnits(
            await (
                await getAquariusPool(poolAddress, accountId)
            ).estimateWithdrawImbalanced(toDepositAmounts(tokens, amounts), accountId),
        );
    }

    async getSingleTokenWithdrawEstimate(
        poolId: string,
        tokens: Token[],
        accountShare: string,
    ): Promise<Map<string, string>> {
        const pool = await getAquariusPool(poolId);
        const values = await pool.estimateWithdrawOneCoin(toBaseUnits(accountShare));
        return new Map(
            tokens.map(token => [
                token.contract,
                fromBaseUnits(values[pool.tokens.indexOf(token.contract)], token.decimal),
            ]),
        );
    }

    async getCreationFeeInfo(): Promise<PoolCreationFeeInfo> {
        const fee = await (await getAquariusClient()).poolCreationFee();
        const token = await this.token.parseTokenContractId(fee.token);
        return {
            token,
            constantFee: fromBaseUnits(fee.volatile, token.decimal),
            stableFee: fromBaseUnits(fee.stable, token.decimal),
            concentratedFee: fromBaseUnits(fee.concentrated, token.decimal),
            destination: fee.destination,
        };
    }

    async getPoolReserves(assets: Token[], poolId: string): Promise<Map<string, string>> {
        const pool = await getAquariusPool(poolId);
        const values = await pool.reserves();
        return new Map(
            assets.map(asset => [
                getAssetString(asset),
                fromBaseUnits(values[pool.tokens.indexOf(asset.contract)], asset.decimal),
            ]),
        );
    }

    getDepositTx(
        accountId: string,
        poolAddress: string,
        assets: Token[],
        amounts: Map<string, string>,
    ) {
        return this.build(async () =>
            (await getAquariusPool(poolAddress, accountId)).buildDeposit({
                amounts: toDepositAmounts(assets, amounts),
            }),
        );
    }

    getSwapChainedTx(
        accountId: string,
        base: Token,
        counter: Token,
        path: FindPathResponse,
        amount: string,
        slippage: number,
        isSend: boolean,
        addTrustline = false,
    ) {
        return this.build(async () => {
            const params = {
                from: await toAquariusAsset(base),
                to: await toAquariusAsset(counter),
                slippage,
                ...(isSend
                    ? { amountIn: toBaseUnits(amount, base.decimal) }
                    : { amountOut: toBaseUnits(amount, counter.decimal) }),
            };
            return (await getAquariusClient(accountId))
                .quoteFromPath(params, path)
                .buildTransaction({ addTrustline });
        });
    }

    getScheduleIncentiveTx(
        accountId: string,
        pool: PoolProcessed,
        rewardToken: Token,
        tps: number,
        startDate: number,
        endDate: number,
        chainedXDR: string,
    ) {
        return this.build(async () =>
            (await getAquariusPool(pool.address, accountId)).buildScheduleIncentive({
                token: await toAquariusAsset(rewardToken),
                tps: toBaseUnits(tps.toFixed(rewardToken.decimal), rewardToken.decimal),
                startAt: BigInt(Math.round(startDate / 1000)),
                durationSeconds: BigInt(Math.round((endDate - startDate) / 1000)),
                swapChainXdr: chainedXDR || undefined,
            }),
        );
    }

    async getIncentivesConfig(): Promise<{ duration: number; minAquaAmount: number }> {
        const config = await (await getAquariusClient()).incentives.config();
        return {
            duration: Number(config.minDurationSeconds),
            minAquaAmount: Number(fromBaseUnits(config.minDailyAmount)),
        };
    }

    async getPoolIncentivesCountPerToken(contractId: string): Promise<number> {
        return (
            scValToNative(
                await this.read(contractId, METHOD.GET_POOL_CONFIG_PER_TOKEN),
            ) as unknown[]
        ).length;
    }

    async getPoolIncentivesMap(poolId: string) {
        return Promise.all(
            (await (await getAquariusPool(poolId)).gauges()).map(async ({ token, gauge }) => ({
                token: await this.token.parseTokenContractId(token),
                incentiveContract: gauge,
                count: await this.getPoolIncentivesCountPerToken(gauge),
            })),
        );
    }

    getChangeUserRewardsStatusTx(poolId: string, accountId: string, rewardsEnabled: boolean) {
        return this.build(async () =>
            (await getAquariusPool(poolId, accountId)).buildSetRewardsEnabled(rewardsEnabled),
        );
    }

    async getUserRewardsStatus(poolId: string, accountId: string): Promise<boolean> {
        return (await getAquariusPool(poolId, accountId)).rewardsEnabled(accountId);
    }

    async estimateDeposit(
        account: string,
        poolId: string,
        assets: Token[],
        amounts: Map<string, string>,
    ) {
        const value = await this.read(
            poolId,
            METHOD.ESTIMATE_DEPOSIT,
            [toTokenAmountsScVal(this.token.orderTokens(assets), amounts)],
            account,
        );
        return Number(fromBaseUnits(BigInt(scValToNative(value))));
    }

    async estimateWorkingBalanceAndSupply(
        pool: PoolExtended,
        accountId: string,
        shares: string,
    ): Promise<{ workingBalance: number; workingSupply: number }> {
        const value = await (
            await getAquariusPool(pool.address, accountId)
        ).estimateWorkingBalance(toBaseUnits(shares, pool.share_token_decimals), accountId);
        return {
            workingBalance: Number(fromBaseUnits(value.workingBalance)),
            workingSupply: Number(fromBaseUnits(value.workingSupply)),
        };
    }

    async estimateConcentratedWorkingBalanceAndSupply(
        accountId: string,
        poolId: string,
        tickLower: number,
        tickUpper: number,
        liquidity: string,
    ): Promise<{ workingBalance: number; workingSupply: number }> {
        const value = await (
            await getAquariusPool(poolId, accountId)
        ).estimatePositionWorkingBalance(tickLower, tickUpper, BigInt(liquidity), accountId);
        return {
            workingBalance: Number(fromBaseUnits(value.workingBalance)),
            workingSupply: Number(fromBaseUnits(value.workingSupply)),
        };
    }

    async getConcentratedPoolInfo(poolId: string): Promise<ConcentratedPoolInfo> {
        return scValToNative(await this.read(poolId, METHOD.GET_INFO));
    }

    async getConcentratedSlot0(poolId: string): Promise<ConcentratedSlot0> {
        const value = await (await getAquariusPool(poolId)).slot0();
        return { sqrt_price_x96: value.sqrtPriceX96.toString(), tick: value.tick };
    }

    async getConcentratedTickSpacing(poolId: string): Promise<number> {
        return (await getAquariusPool(poolId)).tickSpacing();
    }

    async getUserPositionSnapshot(
        poolId: string,
        user: string,
    ): Promise<ConcentratedUserPositionSnapshot | null> {
        return scValToNative(
            await this.read(poolId, METHOD.GET_USER_POSITION_SNAPSHOT, [publicKeyToScVal(user)]),
        );
    }

    async getPosition(
        poolId: string,
        owner: string,
        tickLower: number,
        tickUpper: number,
    ): Promise<ConcentratedPosition | null> {
        const value = await (
            await getAquariusPool(poolId, owner)
        ).position(tickLower, tickUpper, owner);
        return value ? { tickLower, tickUpper, liquidity: value.liquidity.toString() } : null;
    }

    async estimateDepositPosition(
        accountId: string,
        poolId: string,
        tokens: Token[],
        tickLower: number,
        tickUpper: number,
        desiredAmounts: Map<string, string>,
    ): Promise<{ amounts: string[]; liquidity: string }> {
        const pool = await getAquariusPool(poolId, accountId);
        const estimate = await pool.estimatePositionDeposit({
            amounts: toDepositAmounts(tokens, desiredAmounts),
            tickRange: [tickLower, tickUpper],
        });
        return {
            amounts: amountsForTokens(estimate.amounts, pool.tokens, tokens),
            liquidity: estimate.liquidity.toString(),
        };
    }

    getDepositPositionTx(
        accountId: string,
        poolAddress: string,
        tokens: Token[],
        tickLower: number,
        tickUpper: number,
        desiredAmounts: Map<string, string>,
        minLiquidity: string,
    ): Promise<Transaction> {
        return this.build(async () =>
            (
                await (
                    await getAquariusPool(poolAddress, accountId)
                ).estimatePositionDeposit({
                    amounts: toDepositAmounts(tokens, desiredAmounts),
                    tickRange: [tickLower, tickUpper],
                })
            ).buildTransaction({ minimumLiquidity: BigInt(minLiquidity) }),
        );
    }

    async estimateWithdrawPosition(
        accountId: string,
        poolId: string,
        tokens: Token[],
        tickLower: number,
        tickUpper: number,
        liquidity: string,
    ): Promise<string[]> {
        const result = await this.read(
            poolId,
            METHOD.ESTIMATE_WITHDRAW_POSITION,
            [
                publicKeyToScVal(accountId),
                tickToScVal(tickLower),
                tickToScVal(tickUpper),
                amountToUint128(liquidity, 0),
            ],
            accountId,
        );
        const ordered = this.token.orderTokens(tokens);
        return amountsForTokens(
            scValToNative(result),
            ordered.map(token => token.contract),
            tokens,
        );
    }

    getWithdrawPositionTx(
        accountId: string,
        poolId: string,
        tokens: Token[],
        tickLower: number,
        tickUpper: number,
        liquidity: string,
        minAmounts: Map<string, string>,
    ): Promise<Transaction> {
        return this.build(async () =>
            (await getAquariusPool(poolId, accountId)).buildWithdrawPosition({
                tickLower,
                tickUpper,
                liquidity: BigInt(liquidity),
                minimumAmounts: toDepositAmounts(tokens, minAmounts),
            }),
        );
    }

    async getPositionFees(
        poolId: string,
        accountId: string,
        tokens: Token[],
        tickLower: number,
        tickUpper: number,
    ): Promise<string[]> {
        const pool = await getAquariusPool(poolId, accountId);
        return amountsForTokens(
            await pool.positionFees(tickLower, tickUpper, accountId),
            pool.tokens,
            tokens,
        );
    }

    async getAllPositionFees(
        poolId: string,
        accountId: string,
        tokens: Token[],
    ): Promise<string[]> {
        const pool = await getAquariusPool(poolId, accountId);
        return amountsForTokens(await pool.allPositionFees(accountId), pool.tokens, tokens);
    }

    getClaimPositionFeesTx(
        accountId: string,
        poolId: string,
        tickLower: number,
        tickUpper: number,
    ): Promise<Transaction> {
        return this.build(async () =>
            (await getAquariusPool(poolId, accountId)).buildClaimPositionFees(tickLower, tickUpper),
        );
    }

    getClaimAllPositionFeesTx(accountId: string, poolId: string): Promise<Transaction> {
        return this.build(async () =>
            (await getAquariusPool(poolId, accountId)).buildClaimAllPositionFees(),
        );
    }
}
